import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Table2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import { useArtifactCardActions } from "./ArtifactCardActions";
import { ArtifactCode } from "./ArtifactCode";
import { ArtifactLineage } from "./ArtifactLineage";
import { ArtifactToolbar } from "./ArtifactToolbar";

interface ArtifactShellProps {
  preview: ArtifactPreview;
  // The body — type-specific content (table, image, JSON, etc.). Per-type
  // renderers are responsible for nothing but this slot.
  children: ReactNode;
  // Optional larger / richer view rendered inside a modal when the user
  // clicks the toolbar's expand action. When omitted, expand is hidden.
  // Renderers use this for "full data" (dataframe) or "true size"
  // (figure, image) views.
  expandedContent?: ReactNode;
  // Opens the artifact as an app in the session side panel; hidden when omitted.
  onOpenAsApp?: () => void;
  className?: string;
}

// Artifact types that have meaningful upstream provenance. Value/text/blob
// are either terminal or have no notion of provenance in this system.
const LINEAGE_TYPES: ReadonlySet<ArtifactPreview["artifact_type"]> = new Set([
  "dataframe",
  "figure",
]);

const TYPE_LABELS: Record<string, string> = {
  dataframe: "Table",
  figure: "Chart",
  image: "Image",
  value: "Value",
  text: "File",
  blob: "File",
  excel: "Sheet",
  table: "Table",
};

// Type badge that names the source file, grounding the card as an artifact.
// Excel green sheet-grid glyph next to the filename in bold. Other types don't
// carry a badge yet.
const ArtifactBadge = ({ preview }: { preview: ArtifactPreview }) => {
  if (preview.artifact_type !== "excel") return null;
  const filename = preview.filename ?? preview.artifact_name;
  return (
    <div className="flex h-9 min-w-0 items-center gap-1.5 text-sm">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#107C41] text-white">
        <Table2 className="size-4" aria-hidden />
      </span>
      {filename && <span className="truncate font-semibold">{filename}</span>}
    </div>
  );
};

type View = "data" | "code" | "lineage";

interface ArtifactTabsProps {
  view: View;
  onViewChange: (v: View) => void;
  typeLabel: string;
  hasCode: boolean;
  hasLineage: boolean;
}

const ArtifactTabs = ({
  view,
  onViewChange,
  typeLabel,
  hasCode,
  hasLineage,
}: ArtifactTabsProps) => (
  <Tabs value={view} onValueChange={(v) => onViewChange(v as View)}>
    <TabsList>
      <TabsTrigger value="data">{typeLabel}</TabsTrigger>
      {hasCode && <TabsTrigger value="code">Code</TabsTrigger>}
      {hasLineage && <TabsTrigger value="lineage">Lineage</TabsTrigger>}
    </TabsList>
  </Tabs>
);

interface ArtifactViewContentProps {
  view: View;
  code: string | null;
  artifactId: string;
  children: ReactNode; // rendered when view === "data"
}

const ArtifactViewContent = ({ view, code, artifactId, children }: ArtifactViewContentProps) => {
  if (view === "code" && code) {
    return <ArtifactCode code={code} />;
  }
  if (view === "lineage") {
    return <ArtifactLineage artifactId={artifactId} />;
  }
  return <>{children}</>;
};

// Null when the shell has no expandedContent and therefore no modal to open.
const ArtifactExpandContext = createContext<(() => void) | null>(null);

export const useArtifactExpand = (): (() => void) | null => useContext(ArtifactExpandContext);

// How a shell draws its artifact. "card" is the full card; "thumbnail" and
// "expanded" draw only the body or the expanded view, for a surface that
// supplies its own chrome, such as the lineage panel.
export type ArtifactLayout = "card" | "thumbnail" | "expanded";

const ArtifactLayoutContext = createContext<ArtifactLayout>("card");

export const useArtifactLayout = (): ArtifactLayout => useContext(ArtifactLayoutContext);

export const ArtifactLayoutProvider = ({
  layout,
  children,
}: {
  layout: ArtifactLayout;
  children: ReactNode;
}) => <ArtifactLayoutContext.Provider value={layout}>{children}</ArtifactLayoutContext.Provider>;

// Uniform card chrome around every artifact. Layout:
//
//   ┌──────────────────────────────────────────────────────┐
//   │ name               [Lineage] | [share] [↓] [⤢]       │  ← header
//   ├──────────────────────────────────────────────────────┤
//   │ <body>                                               │
//   │ summary (italic, no divider — visually grouped       │
//   │   with the body it describes)                        │
//   └──────────────────────────────────────────────────────┘
//
// Where the surface has a lineage side panel, Lineage opens it and the panel
// carries the code, step by step. Elsewhere (share-link and admin views) the
// header falls back to tabs that swap the body for its code or an inline
// lineage diagram. Tabs that don't apply (no code, non-derived type) are
// hidden.
export const ArtifactShell = ({
  preview,
  children,
  expandedContent,
  onOpenAsApp,
  className,
}: ArtifactShellProps) => {
  const [view, setView] = useState<View>("data");
  const [expandOpen, setExpandOpen] = useState(false);
  const requestExpand = useCallback(() => setExpandOpen(true), []);
  const actions = useArtifactCardActions(preview);
  const layout = useArtifactLayout();
  const title = actions?.title ?? preview.artifact_name ?? preview.filename;
  const hasSummary = Boolean(preview.summary) && preview.artifact_type !== "image";
  const isDerived = LINEAGE_TYPES.has(preview.artifact_type);
  const lineagePanel = isDerived ? (actions?.lineage ?? null) : null;
  const hasCode = Boolean(preview.code) && preview.artifact_type !== "image" && !lineagePanel;
  const hasLineage = isDerived && !lineagePanel;
  const cardActions = actions && { ...actions, lineage: lineagePanel };
  const typeLabel = TYPE_LABELS[preview.artifact_type] ?? preview.artifact_type;

  if (layout === "thumbnail") return <>{children}</>;
  if (layout === "expanded") return <>{expandedContent ?? children}</>;

  return (
    <div
      className={cn(
        "my-3 overflow-hidden rounded-lg border border-border bg-background/60",
        className
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <ArtifactBadge preview={preview} />
        {actions && title && preview.artifact_type !== "excel" && (
          <span className="min-w-0 truncate text-sm font-medium" title={title}>
            {title}
          </span>
        )}
        {(hasCode || hasLineage) && (
          <ArtifactTabs
            view={view}
            onViewChange={setView}
            typeLabel={typeLabel}
            hasCode={hasCode}
            hasLineage={hasLineage}
          />
        )}
        <div className="ml-auto shrink-0">
          <ArtifactToolbar
            preview={preview}
            actions={cardActions}
            onExpand={expandedContent ? requestExpand : undefined}
            onOpenAsApp={onOpenAsApp}
          />
        </div>
      </div>
      <div className="px-3 py-4">
        <ArtifactViewContent view={view} code={preview.code} artifactId={preview.artifact_id}>
          <ArtifactExpandContext.Provider value={expandedContent ? requestExpand : null}>
            {children}
          </ArtifactExpandContext.Provider>
        </ArtifactViewContent>
      </div>
      {hasSummary && (
        <div className="px-3 pb-2 text-[11px] italic leading-snug text-muted-foreground">
          {preview.summary}
        </div>
      )}
      {expandedContent && (
        <Dialog open={expandOpen} onOpenChange={setExpandOpen}>
          <DialogContent
            className="flex h-[calc(100vh-48px)] w-[calc(100vw-48px)] max-w-none flex-col gap-0 overflow-hidden p-4 pt-2 sm:max-w-none"
            overlayClassName="bg-black/70"
          >
            <DialogTitle className="sr-only">{title ?? preview.artifact_type}</DialogTitle>
            <DialogDescription className="sr-only">
              {preview.summary ?? `Expanded view of ${title ?? preview.artifact_type}`}
            </DialogDescription>
            <div className="flex shrink-0 items-center gap-2 border-b border-border py-2">
              <ArtifactBadge preview={preview} />
              {actions && title && preview.artifact_type !== "excel" && (
                <span className="min-w-0 truncate text-base font-semibold">{title}</span>
              )}
              {(hasCode || hasLineage) && (
                <ArtifactTabs
                  view={view}
                  onViewChange={setView}
                  typeLabel={typeLabel}
                  hasCode={hasCode}
                  hasLineage={hasLineage}
                />
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-auto pt-4">
              <ArtifactViewContent view={view} code={preview.code} artifactId={preview.artifact_id}>
                <>
                  {expandedContent}
                  {hasSummary && (
                    <div className="mt-3 px-1 text-[11px] italic leading-snug text-muted-foreground">
                      {preview.summary}
                    </div>
                  )}
                </>
              </ArtifactViewContent>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
